import { useState } from 'react';
import type { FormEvent } from 'react';
import { AuthShell } from '@/components/auth/AuthShell';
import { FormField } from './Login';
import { useAuth } from '@/hooks/useAuth';
import { entryUrlHadEmailLink } from '@/api/supabaseClient';

/**
 * Reset password · /reset-password · slot slot_supabase_migration · SM.3.
 *
 * The reset email link establishes a Supabase recovery session via
 * detectSessionInUrl. While that resolves we show a brief loading state; once
 * a session exists the new-password form renders; with no session the link is
 * treated as invalid/expired. `auth.resetPassword` calls updateUser on the
 * active recovery session. `token` is kept in props for router compatibility
 * but is no longer used (supabase-js carries the token in the URL itself).
 *
 * One-shot gate · Genie 6 JUN 2026: a live session alone is NOT enough to
 * render the form — otherwise an already-used email link (or manual
 * navigation while logged in) re-offers the password form. The form renders
 * only when THIS page load arrived via a fresh email link (entry URL carried
 * recovery/invite markers · `entryUrlHadEmailLink`).
 */

export interface ResetPasswordScreenProps {
  token: string;
  onSuccess: () => void;
  onTokenInvalid: () => void;
  /** Test seam · prod default reads the captured entry URL (one-shot links). */
  fromEmailLink?: boolean;
}

export function ResetPasswordScreen({
  onSuccess,
  onTokenInvalid,
  fromEmailLink,
}: ResetPasswordScreenProps) {
  const auth = useAuth();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cameFromLink = fromEmailLink ?? entryUrlHadEmailLink;
  if (!cameFromLink) {
    return (
      <AuthShell title="Ссылка не валидна" subtitle="Ссылка одноразовая. Запроси новую через «Забыли пароль».">
        <button type="button" className="btn btn--primary" onClick={onTokenInvalid}>
          На главную
        </button>
      </AuthShell>
    );
  }

  if (auth.isLoading) {
    return (
      <AuthShell title="Проверяем ссылку…" subtitle="Это займёт пару секунд.">
        <div className="auth-pending">
          <div className="auth-pending__icon" aria-hidden="true">
            🔄
          </div>
        </div>
      </AuthShell>
    );
  }

  if (!auth.isAuthed) {
    return (
      <AuthShell title="Ссылка не валидна" subtitle="Запроси новую через «Забыли пароль».">
        <button type="button" className="btn btn--primary" onClick={onTokenInvalid}>
          Запросить новую ссылку
        </button>
      </AuthShell>
    );
  }

  const tooShort = password.length > 0 && password.length < 8;
  const mismatch = confirm.length > 0 && password !== confirm;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    // Direct checks (not the `.length > 0`-guarded `tooShort`/`mismatch` flags,
    // which are for inline hints): an EMPTY confirm field made `mismatch` false →
    // submit slipped through with no match validation (Genie 6 JUN 2026).
    if (password.length < 8) {
      setError('Пароль должен быть от 8 символов');
      return;
    }
    if (password !== confirm) {
      setError('Пароли не совпадают');
      return;
    }
    setSubmitting(true);
    const res = await auth.resetPassword({ password });
    setSubmitting(false);
    if ('error' in res) {
      setError(res.error);
      return;
    }
    onSuccess();
  };

  return (
    <AuthShell title="Новый пароль" subtitle="Установи новый пароль · войдём автоматически.">
      <form className="auth-form" onSubmit={handleSubmit} noValidate data-testid="reset-form">
        <FormField
          id="reset-password"
          label="Новый пароль"
          type="password"
          autoComplete="new-password"
          required
          maxLength={128}
          value={password}
          onChange={setPassword}
        />
        <FormField
          id="reset-confirm"
          label="Повтори пароль"
          type="password"
          autoComplete="new-password"
          required
          maxLength={128}
          value={confirm}
          onChange={setConfirm}
        />
        {tooShort && (
          <p className="auth-form__hint" role="status">
            Ещё {8 - password.length} символов до минимума.
          </p>
        )}
        {mismatch && (
          <p className="auth-form__hint" role="status">
            Пароли пока не совпадают.
          </p>
        )}
        <div className="auth-form__error-slot" aria-live="polite">
          {error && (
            <p className="auth-form__error" role="alert" data-testid="reset-error">
              {error}
            </p>
          )}
        </div>
        <button
          type="submit"
          className="btn btn--primary auth-form__submit"
          disabled={submitting || password.length < 8 || password !== confirm}
        >
          {submitting ? 'Сохраняем…' : 'Сохранить пароль'}
        </button>
      </form>
    </AuthShell>
  );
}
